import { useState, useEffect, useRef } from "react"
import { usePageSEO } from "@/lib/usePageSEO"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { Link, useNavigate } from "react-router-dom"
import { Loader2 } from "lucide-react"
import { useMutation } from "@tanstack/react-query"
import { toast } from "sonner"

import { authService } from "@/features/auth/services/authService"
import { useAuthStore } from "@/stores/authStore"
import { APP_NAME } from "@/lib/appConfig"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

const schema = z.object({
  full_name: z.string().min(1, "Full name is required"),
  email: z.string().email("Enter a valid email address"),
  password: z.string().min(8, "Password must be at least 8 characters"),
  country_code: z.string().optional(),
  default_currency: z.string().optional(),
  timezone: z.string().optional(),
})

type FormValues = z.infer<typeof schema>

function OtpScreen({ email }: { email: string }) {
  const navigate = useNavigate()
  const setAuth = useAuthStore((s) => s.setAuth)
  const [otp, setOtp] = useState("")
  const [countdown, setCountdown] = useState(60)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setCountdown((c) => (c > 0 ? c - 1 : 0))
    }, 1000)
    return () => { if (intervalRef.current) clearInterval(intervalRef.current) }
  }, [])

  const verifyMutation = useMutation({
    mutationFn: (code: string) => authService.verifyOtp(email, code),
    onSuccess: (res) => {
      setAuth(res.user, res.token)
      toast.success(`Email verified! Welcome to ${APP_NAME}.`)
      navigate("/dashboard")
    },
    onError: (err: unknown) => {
      const axiosError = err as any
      const message = axiosError?.response?.data?.error ?? "Invalid or expired code"
      toast.error(message)
      setOtp("")
    },
  })

  const resendMutation = useMutation({
    mutationFn: () => authService.resendOtp(email),
    onSuccess: () => {
      toast.success("New code sent")
      setCountdown(60)
    },
    onError: (err: unknown) => {
      const axiosError = err as any
      const message = axiosError?.response?.data?.error ?? "Failed to resend code"
      toast.error(message)
    },
  })

  function handleOtpChange(value: string) {
    const digits = value.replace(/\D/g, "").slice(0, 6)
    setOtp(digits)
    if (digits.length === 6) {
      verifyMutation.mutate(digits)
    }
  }

  return (
    <div className="min-h-screen w-full flex bg-background">
      <div className="w-full flex items-center justify-center p-4 py-12">
        <div className="w-full max-w-sm">
          {/* Mobile Logo */}
          <div className="flex lg:hidden items-center justify-center gap-2 mb-8 text-foreground">
            <img src="/app-icon.png" className="h-10 w-10 object-contain" alt={APP_NAME} />
            <span className="font-bold text-2xl tracking-tight">{APP_NAME.toUpperCase()}</span>
          </div>

          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold tracking-tight bg-gradient-to-br from-foreground to-foreground/70 bg-clip-text text-transparent">
              Check your email
            </h1>
            <p className="text-sm text-muted-foreground mt-3">
              We sent a 6-digit code to
            </p>
            <p className="text-sm font-semibold text-foreground mt-1">{email}</p>
          </div>

          <Card className="rounded-3xl shadow-soft border-border/50 bg-card/60 backdrop-blur-xl">
            <CardContent className="pt-6 space-y-6">
              <div className="space-y-1.5">
                <label className="text-sm font-medium">Verification code</label>
                <Input
                  value={otp}
                  onChange={(e) => handleOtpChange(e.target.value)}
                  placeholder="123456"
                  inputMode="numeric"
                  maxLength={6}
                  className="text-center text-2xl tracking-widest h-14"
                  disabled={verifyMutation.isPending}
                  autoFocus
                />
              </div>

              <Button
                className="w-full h-12 rounded-xl shadow-glow transition-all duration-200 active:scale-[0.97]"
                onClick={() => verifyMutation.mutate(otp)}
                disabled={otp.length !== 6 || verifyMutation.isPending}
              >
                {verifyMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  "Verify Email"
                )}
              </Button>

              <div className="text-center">
                {countdown > 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Resend code in {countdown}s
                  </p>
                ) : (
                  <button
                    onClick={() => resendMutation.mutate()}
                    disabled={resendMutation.isPending}
                    className="text-sm text-primary hover:underline disabled:opacity-50"
                  >
                    {resendMutation.isPending ? "Sending..." : "Resend code"}
                  </button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default function RegisterPage() {
  const [pendingEmail, setPendingEmail] = useState<string | null>(null)
  const [showRegionSettings, setShowRegionSettings] = useState(false)
  const [apiCountries, setApiCountries] = useState<{ code: string, name: string }[]>([])
  const [apiCurrencies, setApiCurrencies] = useState<{ code: string, name: string }[]>([])
  const [countryToCurrency, setCountryToCurrency] = useState<Record<string, string>>({})

  usePageSEO({
    title: `Create Account | ${APP_NAME}`,
    description: `Create your free ${APP_NAME} account. Manage properties, tenants, leases, and finances — no credit card required.`,
    path: "/register",
    // Not prerendered (prerender.js skips auth routes), so a crawler would only
    // ever see an empty shell here.
    noindex: true,
  })

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      full_name: "",
      email: "",
      password: "",
      country_code: "",
      default_currency: "EUR",
      timezone: "UTC",
    },
  })

  useEffect(() => {
    async function loadInitialData() {
      try {
        const [countriesRes, geoRes] = await Promise.allSettled([
          fetch("https://restcountries.com/v3.1/all?fields=name,cca2,currencies"),
          fetch("https://get.geojs.io/v1/ip/geo.json")
        ])

        let c2c: Record<string, string> = {}
        
        if (countriesRes.status === "fulfilled") {
          const data: any[] = await countriesRes.value.json()
          
          const countriesList = data.map((d: any) => ({
            code: d.cca2,
            name: d.name.common
          })).sort((a: any, b: any) => a.name.localeCompare(b.name))

          const currencyMap = new Map<string, string>()

          data.forEach((d: any) => {
            if (d.currencies) {
              const firstCurrency = Object.keys(d.currencies)[0]
              c2c[d.cca2] = firstCurrency
              Object.keys(d.currencies).forEach(cCode => {
                currencyMap.set(cCode, d.currencies[cCode].name)
              })
            }
          })

          const currenciesList = Array.from(currencyMap.entries()).map(([code, name]) => ({ code, name }))
          const priorityOrder = ["USD", "EUR", "GBP", "CAD", "AUD"]
          currenciesList.sort((a, b) => {
            const aIndex = priorityOrder.indexOf(a.code)
            const bIndex = priorityOrder.indexOf(b.code)
            if (aIndex !== -1 && bIndex !== -1) return aIndex - bIndex
            if (aIndex !== -1) return -1
            if (bIndex !== -1) return 1
            return a.code.localeCompare(b.code)
          })

          setApiCountries(countriesList)
          setApiCurrencies(currenciesList)
          setCountryToCurrency(c2c)
        }

        let detectedCountry = "US"
        let detectedTimezone = "UTC"
        if (geoRes.status === "fulfilled") {
          try {
            const geoData = await geoRes.value.json()
            if (geoData && geoData.country_code) {
              detectedCountry = geoData.country_code
            }
            if (geoData && geoData.timezone) {
              detectedTimezone = geoData.timezone
            }
          } catch(e) {}
        }

        // Only set values if the user hasn't modified them already
        if (!form.getValues("country_code")) {
          form.setValue("country_code", detectedCountry)
          form.setValue("timezone", detectedTimezone)
          // Look up in the freshly built c2c map, fallback to USD if missing
          const matchedCurrency = c2c[detectedCountry] || "USD"
          form.setValue("default_currency", matchedCurrency)
        }

      } catch (err) {
        console.error("Failed to load initial region data", err)
      }
    }
    loadInitialData()
  }, [form])

  const { mutate, isPending } = useMutation({
    mutationFn: (values: FormValues) => authService.register(values),
    onSuccess: (_, variables) => {
      setPendingEmail(variables.email)
    },
    onError: (err: unknown) => {
      const axiosError = err as any
      const message =
        axiosError?.response?.data?.error ??
        (err instanceof Error ? err.message : "Registration failed. Please try again.")
      toast.error(message)
    },
  })

  if (pendingEmail) {
    return <OtpScreen email={pendingEmail} />
  }

  return (
    <div className="min-h-screen w-full flex bg-background">
      {/* Left side: Premium Gradient Mesh (Desktop Only) */}
      <div className="hidden lg:flex w-1/2 relative flex-col justify-between p-12 overflow-hidden bg-foreground">
        {/* Animated Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/80 via-primary/30 to-background opacity-40 mix-blend-screen" />

        {/* Logo */}
        <div className="relative z-10 flex items-center gap-3 text-white">
          <img src="/app-icon.png" className="h-10 w-10 object-contain" alt={APP_NAME} />
          <span className="font-bold text-2xl tracking-tight">{APP_NAME.toUpperCase()}</span>
        </div>

        {/* Glassmorphic Testimonial */}
        <div className="relative z-10 max-w-md">
          <div className="bg-white/5 backdrop-blur-2xl border border-white/10 p-8 rounded-3xl shadow-2xl">
            <p className="text-white text-xl font-medium leading-relaxed mb-6">
              "Joining {APP_NAME} saved us hours every week. Setup took minutes and the interface is an absolute pleasure to use."
            </p>
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-white/20 border border-white/20" />
              <div>
                <p className="text-white font-semibold">David Chen</p>
                <p className="text-white/70 text-sm">Real Estate Investor</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side: Form Details */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="flex lg:hidden items-center justify-center gap-2 mb-8 text-foreground">
            <img src="/app-icon.png" className="h-10 w-10 object-contain" alt={APP_NAME} />
            <span className="font-bold text-2xl tracking-tight">{APP_NAME.toUpperCase()}</span>
          </div>

          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold tracking-tight bg-gradient-to-br from-foreground to-foreground/70 bg-clip-text text-transparent">
              Create your account
            </h1>
            <p className="text-sm text-muted-foreground mt-2">
              Start managing your properties today
            </p>
          </div>

          <Card className="rounded-3xl shadow-soft border-border/50 bg-card/60 backdrop-blur-xl">
            <CardContent className="pt-6">
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit((v) => mutate(v))}
                  className="space-y-4"
                >
                  <FormField
                    control={form.control}
                    name="full_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Full Name</FormLabel>
                        <FormControl>
                          <Input
                            autoComplete="name"
                            placeholder="Jane Smith"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input
                            type="email"
                            autoComplete="email"
                            placeholder="you@example.com"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Password</FormLabel>
                        <FormControl>
                          <Input
                            type="password"
                            autoComplete="new-password"
                            placeholder="Min. 8 characters"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {!showRegionSettings ? (
                    <div className="flex items-center justify-between p-4 rounded-xl border border-border/50 bg-card/60">
                      <div>
                        <p className="text-sm font-medium">Region & Currency</p>
                        <p className="text-sm text-muted-foreground mt-0.5">
                          {apiCountries.find(c => c.code === form.watch("country_code"))?.name || form.watch("country_code") || "Loading..."} • {form.watch("default_currency") || "..."}
                        </p>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowRegionSettings(true)}
                      >
                        Change
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-4 p-4 rounded-xl border border-border/50 bg-card/60">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium">Region Settings</p>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowRegionSettings(false)}
                        >
                          Done
                        </Button>
                      </div>
                      <FormField
                        control={form.control}
                        name="country_code"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-xs">Country</FormLabel>
                            <Select
                              onValueChange={(val) => {
                                field.onChange(val)
                                if (countryToCurrency[val]) {
                                  form.setValue("default_currency", countryToCurrency[val])
                                }
                              }}
                              value={field.value}
                            >
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select country" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {apiCountries.map((c) => (
                                  <SelectItem key={c.code} value={c.code}>
                                    {c.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="default_currency"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-xs">Currency</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue placeholder="Select currency" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {apiCurrencies.map((c) => (
                                  <SelectItem key={c.code} value={c.code}>
                                    {c.code} — {c.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}

                  <Button
                    type="submit"
                    className="w-full h-12 rounded-xl mt-4 shadow-glow transition-all duration-200 active:scale-[0.97]"
                    disabled={isPending}
                  >
                    {isPending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Creating account...
                      </>
                    ) : (
                      "Create Account"
                    )}
                  </Button>
                </form>
              </Form>
            </CardContent>
          </Card>

          <p className="text-center text-sm text-muted-foreground mt-8">
            Already have an account?{" "}
            <Link to="/login" className="text-primary font-medium hover:underline">
              Sign in
            </Link>
          </p>

          <p className="text-center text-xs text-muted-foreground/60 mt-4">
            {APP_NAME} &middot; A product of{" "}
            <a href="https://soulspark.games/" target="_blank" rel="noopener noreferrer" className="hover:underline">
              SoulSpark Games
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}
